import { Injectable, effect, signal } from '@angular/core';

export type AppTheme = 'dark' | 'light';

const STORAGE_KEY = 'app-theme';

/** App-wide dark/light theme switch.
 *
 * The app's job/gig portal pages were designed dark-first (#000c18); this
 * service adds an opt-in light theme on top without touching any existing
 * dark styling. The active theme is written to `data-theme` on `<html>` so
 * plain CSS (`html[data-theme='light'] ...`) can layer light overrides —
 * see the "Light theme" section in styles.scss and the per-page .scss files
 * that use `:host-context(html[data-theme='light'])`.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<AppTheme>(this.readInitial());

  constructor() {
    effect(() => {
      const theme = this.theme();
      document.documentElement.setAttribute('data-theme', theme);
      try {
        localStorage.setItem(STORAGE_KEY, theme);
      } catch {
        // Storage can be unavailable (private browsing/quota) — theme still
        // applies for the current session, it just won't persist.
      }
    });
  }

  toggle(): void {
    this.theme.update((current) => (current === 'dark' ? 'light' : 'dark'));
  }

  setTheme(theme: AppTheme): void {
    this.theme.set(theme);
  }

  private readInitial(): AppTheme {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // ignore — fall back to the default below
    }
    return 'dark';
  }
}
